"use client";

import { Fragment, memo, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Menu,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import GenericIcon from "./GenericIcon";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import WithMenu from "./WithMenu";
import WithMenuItem from "./WithMenuItem";
import type { GenericActionBarAction } from "./actionBar.types";
import {
  getActionToneBg,
  getActionToneColor,
  getActionToneHoverBg,
} from "./actionTone";

const MOBILE_MAX_INLINE_ACTIONS = 3;

function getMobileInlineActions(
  actions: GenericActionBarAction[],
): GenericActionBarAction[] {
  const inlineCandidates = actions.filter(
    (action) => (action.mobileBehavior ?? "inline") === "inline",
  );

  return inlineCandidates.slice(0, MOBILE_MAX_INLINE_ACTIONS);
}

function sortActionsByPriority(
  actions: GenericActionBarAction[],
): GenericActionBarAction[] {
  return [...actions].sort(
    (left, right) => (left.priority ?? 100) - (right.priority ?? 100),
  );
}

function renderMenuActionLabel(action: GenericActionBarAction) {
  const actionColor = getActionToneColor(action.tone);

  return (
    <HStack gap="2" align="center" color={actionColor}>
      <GenericIcon
        icon={action.icon}
        size={iconSizePresets.menuItem}
        color={actionColor}
        humanReadable={action.humanReadable}
      />
      <Text color={actionColor}>{action.label}</Text>
    </HStack>
  );
}

function shouldRenderSeparatorAfterAction(
  action: GenericActionBarAction,
  actionIndex: number,
  actions: GenericActionBarAction[],
  hasTrailingActions = false,
) {
  return (
    action.separatorAfter &&
    (actionIndex < actions.length - 1 || hasTrailingActions)
  );
}

function ActionGroupSeparator() {
  return (
    <Box alignSelf="stretch" width="1px" bg="moveon.surface.border" mx="1" />
  );
}

function SelectionActionButton({
  action,
  collapsed = false,
}: {
  action: GenericActionBarAction;
  collapsed?: boolean;
}) {
  const actionColor = getActionToneColor(action.tone);

  return (
    <Button
      variant="surface"
      color={actionColor}
      bg={getActionToneBg(action.tone)}
      borderWidth="1"
      borderColor="moveon.surface.interactiveBorder"
      _hover={{
        bg: getActionToneHoverBg(action.tone) ?? "moveon.surface.elevatedBg",
      }}
      size="sm"
      onClick={action.onSelect}
      disabled={action.disabled}
      aria-label={action.humanReadable}
      title={action.humanReadable}
      justifyContent={collapsed ? "center" : "flex-start"}
      minW={collapsed ? "10" : "0"}
      w="full"
      px={collapsed ? "0" : "2.5"}
    >
      <GenericIcon
        icon={action.icon}
        size={iconSizePresets.actionButton}
        humanReadable={action.humanReadable}
      />
      {collapsed ? null : (
        <Text as="span" lineClamp={1}>
          {action.shortLabel ?? action.label}
        </Text>
      )}
    </Button>
  );
}

export type GenericActionBarProps = {
  selectedItemsCount: number;
  handlingWhatThoughts?: "thoughts" | "suggested_thoughts" | "origins";
  selectedSituationsCount?: number;
  selectionLabel?: string;
  contextLabel?: string | null;
  open?: boolean;
  actions?: GenericActionBarAction[];
  /** Unselect handler */
  onUnselect?: () => void;
  unselectButtonLabel?: string;
  /** Delete handler */
  onDelete?: () => Promise<void>;
  deleteButtonLabel?: string;
  /** Reset reflections handler */
  onReset?: () => void;
  resetButtonLabel?: string;
  /** Confirm thoughts handler */
  onConfirm?: () => Promise<void>;
  confirmButtonLabel?: string;
};

function GenericActionBarInner({
  selectedItemsCount,
  handlingWhatThoughts = "thoughts",
  selectedSituationsCount,
  selectionLabel,
  contextLabel,
  open,
  actions,
  onUnselect,
  unselectButtonLabel = "Unselect",
  onDelete,
  deleteButtonLabel = "Delete",
  onReset,
  resetButtonLabel = "Reset",
  onConfirm,
  confirmButtonLabel = "Confirm",
}: GenericActionBarProps) {
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(true);
  const thoughtSingularLabel =
    handlingWhatThoughts === "thoughts"
      ? "thought"
      : handlingWhatThoughts === "suggested_thoughts"
        ? "suggested thought"
        : "origin";

  const thoughtPluralLabel =
    handlingWhatThoughts === "thoughts"
      ? "thoughts"
      : handlingWhatThoughts === "suggested_thoughts"
        ? "suggested thoughts"
        : "origins";

  const resolvedSelectionLabel =
    selectionLabel ??
    `${selectedItemsCount} ${
      selectedItemsCount === 1 ? thoughtSingularLabel : thoughtPluralLabel
    }${
      selectedSituationsCount && selectedSituationsCount > 0
        ? ` @ ${selectedSituationsCount} ${
            selectedSituationsCount === 1 ? "situation" : "situations"
          }`
        : ""
    }`;

  const resolvedActions = useMemo<GenericActionBarAction[]>(
    () =>
      actions ??
      ([
        onReset && {
          key: "reset",
          label: resetButtonLabel,
          shortLabel: resetButtonLabel,
          humanReadable: `${resetButtonLabel} selected ${thoughtPluralLabel}`,
          icon: "reset",
          tone: "error" as const,
          onSelect: onReset,
        },
        onConfirm && {
          key: "confirm",
          label: confirmButtonLabel,
          shortLabel: confirmButtonLabel,
          humanReadable: `${confirmButtonLabel} selected ${thoughtPluralLabel}`,
          icon: "confirm",
          tone: "success" as const,
          onSelect: onConfirm,
        },
        onUnselect && {
          key: "unselect",
          label: unselectButtonLabel,
          shortLabel: unselectButtonLabel,
          humanReadable: `${unselectButtonLabel} selected ${thoughtPluralLabel}`,
          icon: "unselect",
          tone: "warning" as const,
          onSelect: onUnselect,
        },
        onDelete && {
          key: "delete",
          label: deleteButtonLabel,
          shortLabel: deleteButtonLabel,
          humanReadable: `${deleteButtonLabel} selected ${thoughtPluralLabel}`,
          icon: "delete_permanent",
          tone: "error" as const,
          onSelect: onDelete,
        },
      ].filter(Boolean) as GenericActionBarAction[]),
    [
      actions,
      confirmButtonLabel,
      deleteButtonLabel,
      onConfirm,
      onDelete,
      onReset,
      onUnselect,
      resetButtonLabel,
      thoughtPluralLabel,
      unselectButtonLabel,
    ],
  );

  const isOpen =
    open ?? selectedItemsCount + (selectedSituationsCount || 0) > 0;

  const visibleActions = useMemo(
    () => resolvedActions.filter((action) => !action.hidden),
    [resolvedActions],
  );

  const clearSelectionAction = useMemo(
    () =>
      visibleActions.find((action) => action.key === "unselect_visible") ??
      visibleActions.find((action) => action.key === "unselect_all") ??
      visibleActions.find((action) => action.key === "unselect"),
    [visibleActions],
  );

  const contextualActions = useMemo(
    () =>
      visibleActions.filter(
        (action) => action.key !== clearSelectionAction?.key,
      ),
    [clearSelectionAction?.key, visibleActions],
  );

  const prioritizedActions = useMemo(
    () => sortActionsByPriority(contextualActions),
    [contextualActions],
  );

  const mobileInlineActions = useMemo(
    () => getMobileInlineActions(prioritizedActions),
    [prioritizedActions],
  );

  const mobileOverflowActions = useMemo(() => {
    const inlineActionKeys = new Set(
      mobileInlineActions.map((action) => action.key),
    );

    return prioritizedActions.filter(
      (action) =>
        (action.mobileBehavior ?? "inline") === "overflow" ||
        !inlineActionKeys.has(action.key),
    );
  }, [mobileInlineActions, prioritizedActions]);
  const hasMobileActionRow =
    mobileInlineActions.length > 0 ||
    mobileOverflowActions.length > 0 ||
    Boolean(clearSelectionAction);
  const hasDesktopActionRow =
    prioritizedActions.length > 0 || Boolean(clearSelectionAction);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Box display={{ base: "block", md: "none" }} w="full">
        <VStack
          align="stretch"
          gap="2"
          bg="moveon.surface.toolbarBg"
          color="moveon.surface.fg"
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderRadius="md"
          px="2"
          py="2"
        >
          <Text
            fontSize="xs"
            fontWeight="semibold"
            lineHeight="short"
            lineClamp={1}
            title={resolvedSelectionLabel}
          >
            {resolvedSelectionLabel}
          </Text>

          <HStack justify="space-between" align="center" gap="2" minW="0">
            <Box minW="0" flex="1">
              {contextLabel ? (
                <Badge
                  variant="surface"
                  colorPalette="blue"
                  maxW="full"
                >
                  <Text as="span" lineClamp={1} title={contextLabel}>
                    {contextLabel}
                  </Text>
                </Badge>
              ) : null}
            </Box>
          </HStack>

          {hasMobileActionRow ? (
            <HStack gap="2" align="stretch" justify="end">
              {mobileInlineActions.map((action, index) => (
                <Fragment key={action.key}>
                  <Button
                    flex="1"
                    minW={0}
                    variant="surface"
                    color={getActionToneColor(action.tone)}
                    bg={getActionToneBg(action.tone)}
                    borderWidth="1"
                    borderColor="moveon.surface.interactiveBorder"
                    _hover={{
                      bg:
                        getActionToneHoverBg(action.tone) ??
                        "moveon.surface.elevatedBg",
                    }}
                    size="sm"
                    onClick={action.onSelect}
                    disabled={action.disabled}
                    aria-label={action.humanReadable}
                    title={action.humanReadable}
                  >
                    <GenericIcon
                      icon={action.icon}
                      size={iconSizePresets.actionButton}
                      humanReadable={action.humanReadable}
                    />
                  </Button>
                  {shouldRenderSeparatorAfterAction(
                    action,
                    index,
                    prioritizedActions,
                    Boolean(clearSelectionAction),
                  ) ? (
                    <ActionGroupSeparator />
                  ) : null}
                </Fragment>
              ))}

              {mobileOverflowActions.length > 0 ? (
                <WithMenu size="sm" placement="bottom" minWidth="56">
                  {mobileOverflowActions.map((action, index) => (
                    <Fragment key={action.key}>
                      <WithMenuItem
                        value={action.key}
                        humanReadable={action.humanReadable}
                        onClick={action.onSelect}
                        disabled={action.disabled}
                        tone={action.tone}
                      >
                        {renderMenuActionLabel(action)}
                      </WithMenuItem>
                      {shouldRenderSeparatorAfterAction(
                        action,
                        index,
                        mobileOverflowActions,
                      ) ? (
                        <Menu.Separator />
                      ) : null}
                    </Fragment>
                  ))}
                </WithMenu>
              ) : null}

              {clearSelectionAction ? (
                <Button
                  variant="ghost"
                  size="sm"
                  color={getActionToneColor(clearSelectionAction.tone)}
                  _hover={{
                    bg:
                      getActionToneHoverBg(clearSelectionAction.tone) ??
                      "moveon.surface.elevatedBg",
                  }}
                  onClick={clearSelectionAction.onSelect}
                  disabled={clearSelectionAction.disabled}
                  aria-label={clearSelectionAction.humanReadable}
                  title={clearSelectionAction.humanReadable}
                >
                  <GenericIcon
                    icon={clearSelectionAction.icon}
                    size={iconSizePresets.actionButton}
                    humanReadable={clearSelectionAction.humanReadable}
                  />
                </Button>
              ) : null}
            </HStack>
          ) : null}
        </VStack>
      </Box>

      <Box
        display={{ base: "none", md: "block" }}
        position="sticky"
        top="4"
        w={isDesktopCollapsed ? "14" : "56"}
        flexShrink="0"
      >
        <VStack
          align="stretch"
          gap="3"
          bg="moveon.surface.toolbarBg"
          color="moveon.surface.fg"
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderRadius="lg"
          boxShadow="sm"
          px={isDesktopCollapsed ? "2" : "3"}
          py="3"
          minH="32"
        >
          <Stack
            direction={isDesktopCollapsed ? "column" : "row"}
            justify={isDesktopCollapsed ? "center" : "space-between"}
            align={isDesktopCollapsed ? "center" : "center"}
            gap={isDesktopCollapsed ? "1.5" : "2"}
          >
            {isDesktopCollapsed ? (
              <Badge
                variant="surface"
                colorPalette="blue"
                minW="8"
                justifyContent="center"
              >
                {selectedItemsCount + (selectedSituationsCount ?? 0)}
              </Badge>
            ) : (
              <Box minW="0" flex="1">
                <Text
                  fontSize="xs"
                  color="moveon.text.muted"
                  fontWeight="medium"
                >
                  Selection
                </Text>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  whiteSpace="normal"
                  title={resolvedSelectionLabel}
                >
                  {resolvedSelectionLabel}
                </Text>
                {contextLabel ? (
                  <Badge
                    mt="1"
                    variant="surface"
                    colorPalette="blue"
                    maxW="full"
                  >
                    <Text as="span" lineClamp={1}>
                      {contextLabel}
                    </Text>
                  </Badge>
                ) : null}
              </Box>
            )}

            <Button
              variant="ghost"
              size="xs"
              minW="7"
              px="0"
              onClick={() => setIsDesktopCollapsed((value) => !value)}
              aria-label={
                isDesktopCollapsed
                  ? "Expand selection actions"
                  : "Collapse selection actions"
              }
              title={
                isDesktopCollapsed
                  ? "Expand selection actions"
                  : "Collapse selection actions"
              }
            >
              <GenericIcon
                icon={isDesktopCollapsed ? "to_right" : "to_left"}
                size="xs"
                humanReadable={
                  isDesktopCollapsed
                    ? "Expand selection actions"
                    : "Collapse selection actions"
                }
              />
            </Button>
          </Stack>

          {hasDesktopActionRow ? (
            <VStack align="stretch" gap="2">
              {prioritizedActions.map((action) => (
                <SelectionActionButton
                  key={action.key}
                  action={action}
                  collapsed={isDesktopCollapsed}
                />
              ))}

              {clearSelectionAction ? (
                <Box
                  borderTopWidth="1"
                  borderTopColor="moveon.surface.border"
                  pt="2"
                  mt="1"
                >
                  <SelectionActionButton
                    action={clearSelectionAction}
                    collapsed={isDesktopCollapsed}
                  />
                </Box>
              ) : null}
            </VStack>
          ) : null}
        </VStack>
      </Box>
    </>
  );
}

const GenericActionBar = memo(GenericActionBarInner);
export default GenericActionBar;
